import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, of, OperatorFunction, Subscription, throwError } from 'rxjs';
import { catchError, delay, map, switchMap, tap } from 'rxjs/operators';

import { getErrorKey } from 'src/app/helpers/utils/notifications.utils';

import { AnalyticsService } from 'src/app/services/analytics/analytics.service';
import { InternalService } from 'src/app/services/internal/internal.service';
import { NotificationsService } from 'src/app/services/notifications/notifications.service';
import { PaymentsService } from 'src/app/services/payments/payments.service';
import { RoomService } from 'src/app/services/room/room.service';
import { StorageService } from 'src/app/services/storage/storage.service';
import { UrlParametersService } from 'src/app/services/urlParameters/url-parameters.service';

import { bookingSteps, checkoutSteps } from 'src/app/shared/constants/bookingSteps.constant';
import {
  DefaultError,
  ReservationExpiredError,
  ReservationNotPossibleError,
  ReservationRoomReservedError
} from 'src/app/shared/constants/errors.constants';

import { CentreServices, ServiceSelection } from 'src/app/shared/models/centre-service.model';
import { CompleteBookingInfo } from 'src/app/shared/models/payment.model';
import {
  ReservationData,
  ReservationRequestData,
  ReservationValidationStatus
} from 'src/app/shared/models/reservation.model';
import { Room } from 'src/app/shared/models/room.model';

import { environment } from 'src/environments/environment';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  DialogDataModel,
  defaultDialogConfig,
  RgModalComponent
} from 'src/app/shared/components/modal/modal.component';
import { SpaceType } from 'src/app/shared/constants/spaceTypes.constant';
import { DiscountType } from 'src/app/services/time-slot-discounts/time-slot-discounts.service';

export const reservationErrorsByValidationStatus = {
  [ReservationValidationStatus.InventoryAlreadyReserved]: ReservationRoomReservedError,
  [ReservationValidationStatus.InventoryReservationNotPossible]: ReservationNotPossibleError,
  [ReservationValidationStatus.InventoryReservationExpired]: ReservationExpiredError
};

@Injectable({
  providedIn: 'root'
})
export class ReservationService {
  public isCancellingReservation = new BehaviorSubject<boolean>(false);
  public reservationData = new BehaviorSubject<ReservationData>(null);
  public selectedServices = new BehaviorSubject<ServiceSelection[]>([]);
  public timerSecondsLeft = new BehaviorSubject<number>(0);

  public activeReservationId = null;
  public activeReservationSlotId = null;
  public completeBookingInfo: CompleteBookingInfo;
  public preventClearingReservationData = false;
  public reservationInProgress = false;
  public reservationRequestData: ReservationRequestData;

  private timer: Subscription;
  private expirationDialogRef: MatDialogRef<RgModalComponent>;
  private dialogData: DialogDataModel = {
    isClosable: false,
    title: 'reservationTimeoutHeader',
    messages: ['reservationTimeoutMessageLine1', 'reservationTimeoutMessageLine2'],
    actions: [
      {
        btnClass: 'button-dark',
        title: 'reservationTimeoutMessageButton',
        confirmAction: (): void => this.onModalClose()
      }
    ]
  };

  constructor(
    public notificationsService: NotificationsService,
    private analyticsService: AnalyticsService,
    private http: HttpClient,
    private internalService: InternalService,
    private paymentService: PaymentsService,
    private roomService: RoomService,
    private storageService: StorageService,
    private urlParamsService: UrlParametersService,
    private dialogService: MatDialog
  ) {}

  public setTimerSeconds(seconds = 0): void {
    this.timerSecondsLeft.next(seconds);
  }

  public processReservationData(room: Room, reservationData: ReservationData, preventClearing = false): void {
    this.setReservationData(reservationData);
    this.preventClearingReservationData = preventClearing;

    this.roomService.setSelectedRoom(room);
    this.activeReservationId = reservationData.id;
    this.activeReservationSlotId = reservationData.slotId;
    this.internalService.setBookingInProcess(true);
    this.analyticsService.trackEvent({
      eventCategory: 'Button',
      eventAction: 'Click',
      eventLabel: 'Add To Cart',
      city: room.address.city,
      event: 'trackEvent',
      centreName: room.centreName,
      centerNumber: room.centreNumber,
      pageGroup: 'e-commerce'
    });
    this.urlParamsService.navigate({}, [bookingSteps.checkout, checkoutSteps.roomServices]); //todo remove fragment
    this.startTimer(new Date(reservationData.expirationTimeUtc));
  }

  public startReservation(room: Room): Promise<CentreServices> {
    const params = this.roomService.searchParams.value;
    this.reservationRequestData = {
      date: params.date,
      timeFrom: params.timeFrom,
      timeUntil: params.timeUntil,
      numberOfPeople: params.numberOfPeople,
      spaceType: this.urlParamsService.spaceType as SpaceType,
      inventoryId: room.price.inventoryId,
      configurationId: room.price.roomConfigurationId,
      timeSlotType: params.timeSlotType ?? DiscountType.SpecificTime
    };
    this.storageService.remove('servicesSelection');
    this.updateServicesSelection([]);
    this.reservationInProgress = true;

    return new Promise<CentreServices>((resolve, reject) => {
      this.http
        .post(`${environment.apiUrl}rooms/${room.centreNumber}/inventoryreservations`, this.reservationRequestData)
        .pipe(this.handleReservationResponse(), this.handleReservationError())
        .subscribe({
          next: (res: ReservationData) => {
            this.reservationInProgress = false;
            this.processReservationData(room, res);
            sessionStorage.setItem('reservationData', JSON.stringify(res));
            resolve(res.availableServices);
          },
          error: (err) => {
            this.reservationInProgress = false;
            const message = err?.error?.message || err?.message || 'Unknown reservation error';

            reject(new Error(message));
            return throwError(() => new Error(message));
          }
        });
    });
  }

  public repriceReservation(accountId: string, contactId: string): Observable<CentreServices> {
    const centreNumber = this.roomService.selectedRoom.getValue().centreNumber;
    return this.http
      .post(`${environment.apiUrl}rooms/inventoryreservations/${this.activeReservationId}/reprice`, {
        accountId,
        contactId,
        centreNumber
      })
      .pipe(
        this.handleReservationResponse(),
        this.handleReservationError(),
        tap((res: ReservationData): void => {
          this.dropReservationTimer();
          this.startTimer(new Date(res.expirationTimeUtc));
          this.activeReservationId = res.id;
          this.activeReservationSlotId = res.slotId;
          this.setReservationData(res);
        }),
        map((res) => res.availableServices),
        catchError((error) => {
          return throwError(() => new Error(error));
        })
      );
  }

  public dropReservationTimer(): void {
    if (this.timer) {
      this.timer.unsubscribe();
      this.timer = null;
    }
    this.setTimerSeconds(0);
  }

  public dropReservationData(withNavigation = false): Promise<void> {
    return new Promise((resolve) => {
      this.dropReservationTimer();

      const finalizeExpire = (): void => {
        this.roomService.setSelectedRoom(null);
        this.setReservationData(null);
        this.updateServicesSelection([]);
        sessionStorage.removeItem('selectedRoom');
        sessionStorage.removeItem('reservationData');
        this.activeReservationId = null;
        this.activeReservationSlotId = null;

        resolve();
      };
      if (withNavigation) {
        this.urlParamsService.navigate().then(() => {
          finalizeExpire();
        });
      } else {
        finalizeExpire();
      }
    });
  }

  public cancelReservation(): Promise<void> {
    return new Promise((resolve) => {
      if (this.preventClearingReservationData) {
        this.preventClearingReservationData = false;
        resolve();
      } else {
        if (this.activeReservationId) {
          this.isCancellingReservation.next(true);
          return this.http
            .post(`${environment.apiUrl}rooms/inventoryreservations/${this.activeReservationId}/release`, {})
            .subscribe({
              next: () => {
                this.dropReservationData();
                this.isCancellingReservation.next(false);
                resolve();
              },
              error: () => {
                this.isCancellingReservation.next(false);
                // resolve anyway. We don't want to stop flow
                resolve();
              }
            });
        } else {
          resolve();
        }
      }
    });
  }

  public updateServicesSelection(selectedServices: ServiceSelection[]): void {
    this.selectedServices.next(selectedServices);
  }

  public setReservationData(reservationData: ReservationData): void {
    this.reservationData.next(reservationData);
  }

  public updateHostInfo(contactId: string, centreNumber: number): Observable<any> {
    return this.http.put(`${environment.apiUrl}bookings/${this.completeBookingInfo.id}/host`, {
      hostContactId: contactId,
      centreNumber,
      inventoryReservationRequest: this.reservationRequestData,
      confirmInventoryReservationRequest: this.paymentService.bookingPaymentData
    });
  }

  public updateBooking(notesToLocation: string): Observable<any> {
    return this.http.put(`${environment.apiUrl}bookings/${this.completeBookingInfo.id}`, { notesToLocation });
  }

  private handleReservationResponse = (): OperatorFunction<ReservationData, ReservationData> => {
    return switchMap((res: ReservationData) => {
      if (res.validationStatus !== ReservationValidationStatus.Succeeded) {
        const errorKey = reservationErrorsByValidationStatus[res.validationStatus] || DefaultError;
        return throwError(() => new Error(errorKey));
      }

      return of(res);
    });
  };

  private handleReservationError = (): OperatorFunction<unknown, unknown> => {
    return catchError((err) => {
      if (typeof err === 'string') {
        this.notificationsService.pushNotification(err);
      } else {
        this.notificationsService.pushNotification(getErrorKey(err, DefaultError));
      }

      return throwError(err);
    });
  };

  private getTimerSecondsLeft(expirationTime: Date): number {
    const millisecondsInSec = 1000;
    const timeoutSec = new Date(expirationTime).getTime() - Date.now();
    const secondsLeft = Math.round(timeoutSec / millisecondsInSec);

    return secondsLeft;
  }

  private startTimer(expirationTime: Date): void {
    const secondsLeft = this.getTimerSecondsLeft(expirationTime);

    if (secondsLeft < 0) {
      console.error(`Please check expirationTime parameter: ${expirationTime}`);
    }

    this.timer = this.initTimer(expirationTime);
  }

  private initTimer(expirationTime: Date): Subscription | null {
    if (!this.activeReservationId) {
      return null;
    }

    const secondsLeft = this.getTimerSecondsLeft(expirationTime);
    this.setTimerSeconds(secondsLeft < 0 ? 0 : secondsLeft);

    return of(secondsLeft)
      .pipe(
        delay(1000),
        tap(() => {
          if (secondsLeft < 1) {
            this.openExpirationDialog();
          } else {
            this.timer = this.initTimer(expirationTime);
          }
        })
      )
      .subscribe();
  }

  private openExpirationDialog(): void {
    this.expirationDialogRef = this.dialogService.open(RgModalComponent, {
      ...defaultDialogConfig,
      disableClose: true,
      data: this.dialogData
    });
  }

  private onModalClose(): void {
    this.dropReservationData(true).then(() => this.expirationDialogRef.close());
  }
}
